/**
 * Assessment scale-type catalog — VER-108 / Phase 3 (mock).
 *
 * The "different types" surfaced by the new-assessment chooser. Each catalog
 * entry pairs presentation metadata (tags, item count, who it's for) with a
 * runnable {@link SurveyDefinition} so the take screen can render any chosen
 * scale through the EXISTING builder engine (SurveyRunner + scoreSurvey).
 *
 * LICENCE SAFETY — EVERY entry in this catalog is synthetic. Item wording reads
 * "[<code> item N — synthetic placeholder]" and is not any real instrument's text.
 * The catalog once carried two entries with genuine WHO-5 and UCLA-20 wording;
 * those demonstration seeds are deleted, because organisations author their own
 * instruments through the native builder. Do not reintroduce real scale statements
 * here — invent a fake scale if a demo needs more breadth.
 *
 * TODO[VER-108/Phase3]: replace this mock catalog with a real
 *   GET /api/assess/scales response; map each row to a stored SurveyDefinition.
 */
import type { ScaleOption, SurveyDefinition, SurveyQuestion } from "../builder/survey.types";

/** Editorial tag shown as a coloured pill on each scale card. */
export type ScaleTag = "Recommended" | "Clinical";

/** Which populations a scale is best suited to (free-text demo labels). */
export type ScaleAudience = string;

export type ScaleCatalogEntry = {
    /** Stable id used in the /assess/take/:scaleId route. */
    id: string;
    name: string;
    /** Short blurb shown under the title. */
    summary: string;
    tags: ScaleTag[];
    itemCount: number;
    recommendedFor: ScaleAudience[];
    /**
     * The runnable survey for this scale. WHO-5 and UCLA-20 reuse the licensed
     * seed surveys; synthetic scales are generated with placeholder item text.
     */
    survey: SurveyDefinition;
};

// ---------------------------------------------------------------------------
// Synthetic survey builder — placeholder item text ONLY.
// ---------------------------------------------------------------------------

/** Generic 4-point frequency scale used by the synthetic clinical placeholders. */
const SYNTHETIC_SCALE: ScaleOption[] = [
    { label: "Not at all", value: 0 },
    { label: "Several days", value: 1 },
    { label: "More than half the days", value: 2 },
    { label: "Nearly every day", value: 3 },
];

/**
 * Build a synthetic SurveyDefinition with placeholder item text. NEVER reproduces
 * licensed scale wording — every question reads "[<code> item N — placeholder]".
 * Migrated to the VER-109 section-based model: a single all-likert section over a
 * shared scale.
 */
const synthesiseSurvey = (code: string, title: string, instructions: string, itemCount: number): SurveyDefinition => {
    const questions: SurveyQuestion[] = Array.from({ length: itemCount }, (_, i) => ({
        id: `q${i + 1}`,
        order: i + 1,
        text: `[${code} item ${i + 1} — synthetic placeholder, not the licensed statement]`,
        type: "likert",
        reverseScored: false,
        scored: true,
        required: true,
    }));
    const maxOption = Math.max(...SYNTHETIC_SCALE.map((o) => o.value));
    return {
        id: `synthetic-${code.toLowerCase()}`,
        code,
        title,
        instructions,
        higherIsBetter: false,
        status: "published",
        version: 1,
        defaultScale: { options: SYNTHETIC_SCALE },
        sections: [{ id: "section-1", order: 1, title, questions }],
        scoring: {
            method: "sum",
            rawRange: [0, itemCount * maxOption],
        },
        branchingRules: [],
        licence:
            "Synthetic placeholder scale for UI demonstration only. Item text is NOT the licensed instrument — do not use clinically.",
        isSystemSeed: true,
    };
};

// ---------------------------------------------------------------------------
// Catalog
// ---------------------------------------------------------------------------

export const SCALE_CATALOG: ScaleCatalogEntry[] = [
    {
        id: "synthetic-swemwbs",
        name: "SWEMWBS (placeholder)",
        summary: "Short wellbeing scale shape for lower-confidence groups. Synthetic item text for demo only.",
        tags: ["Recommended"],
        itemCount: 7,
        recommendedFor: ["13+ lower-confidence"],
        survey: synthesiseSurvey(
            "SWEMWBS",
            "SWEMWBS (placeholder)",
            "Demonstration survey with synthetic item text. The real SWEMWBS is licensed and delivered separately.",
            7,
        ),
    },
    {
        id: "synthetic-phq9",
        name: "PHQ-9 (placeholder)",
        summary: "Nine-item depression screen shape. Surface only when escalation is warranted.",
        tags: ["Clinical"],
        itemCount: 9,
        recommendedFor: ["18+ depression"],
        survey: synthesiseSurvey(
            "PHQ9",
            "PHQ-9 (placeholder)",
            "Demonstration survey with synthetic item text. The real PHQ-9 is delivered through the clinical pathway.",
            9,
        ),
    },
    {
        id: "synthetic-gad7",
        name: "GAD-7 (placeholder)",
        summary: "Seven-item anxiety screen shape. Surface only when escalation is warranted.",
        tags: ["Clinical"],
        itemCount: 7,
        recommendedFor: ["18+ anxiety"],
        survey: synthesiseSurvey(
            "GAD7",
            "GAD-7 (placeholder)",
            "Demonstration survey with synthetic item text. The real GAD-7 is delivered through the clinical pathway.",
            7,
        ),
    },
];

/** Lookup a catalog entry by its id (the value used in the take route). */
export const findScale = (scaleId: string | undefined): ScaleCatalogEntry | undefined =>
    SCALE_CATALOG.find((s) => s.id === scaleId);
